import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ActivityEntry from "./ActivityEntry";
import { getDateLabel, groupActivitiesByDate } from "./activityHelpers";
import type { EnrichedActivity } from "./FilteredActivityList";

export interface ActivityAccordionProps {
  activities: EnrichedActivity[];
  handleRevert: (key: string, oldValue: any, uniqueId: string | undefined) => Promise<void> | void;
  pendingRevertId?: string | null;
}

export default function ActivityAccordion({
  activities,
  handleRevert,
  pendingRevertId,
}: Readonly<ActivityAccordionProps>) {
  const { grouped: activitiesGroupedByDate, sortedDates } =
    groupActivitiesByDate(activities);

  return (
    <Box>
      {sortedDates.map((dateKey) => {
        const sampleActivity = activitiesGroupedByDate[dateKey][0];
        const dateLabel = sampleActivity.changedAt ? getDateLabel(sampleActivity.changedAt) : dateKey;
        return (
          <Accordion
            key={dateKey}
            defaultExpanded
            disableGutters
            sx={{
              border: "none",
              boxShadow: "none",
              mt: 0,
              pt: 0,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-label={`Activities on ${dateLabel}: ${activitiesGroupedByDate[dateKey].length} items`}
            >
              <Typography
                variant="subtitle2"
                component="h3"
                sx={{ fontWeight: "bold" }}
              >
                {dateLabel}
              </Typography>
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
                role="doc-subtitle"
              >
                ({activitiesGroupedByDate[dateKey].length} changes)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {activitiesGroupedByDate[dateKey].map(
                  (activity: EnrichedActivity) => (
                    <ActivityEntry
                      key={activity.id}
                      activity={activity}
                      handleRevert={handleRevert}
                      isRevertPending={pendingRevertId === activity.id}
                    />
                  ),
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
